import type { AuthState, OktaAuth } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';
import {
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ErrorPage as ErrorScreen } from '~/components/ErrorPage';
import { bff } from '~/lib/bff';
import type { User } from '~/modules/auth';
import { BrandContext } from '~/modules/branding';
import type { Brand } from '~/modules/branding/types';
import { LoadingScreen } from '~/modules/navigation/components/LoadingScreen';

import { getUserProfile } from './utils/getUserProfile';
import { WmgSessionContext } from './WmgSessionContext';

function getUserFactory(
  brand: Brand,
  oktaAuth: OktaAuth,
  authState: AuthState | null,
) {
  // Defined per (memoized) factory rather than inside getUserImpl so it isn't
  // recreated on every call. Refreshing the Okta token lets the BFF
  // re-exchange a brand-new token when the previous exchange was blocked by
  // replay-attack prevention.
  const refreshOktaToken = async () => {
    const { accessToken, idToken, refreshToken } =
      await oktaAuth.token.renewTokens();

    // Persist the renewed tokens to local storage (and sync Okta's authState)
    // via add(), which emits the tokenManager 'added' event. We avoid
    // setTokens() on purpose: it emits 'renewed', which okta-auth.ts wires to
    // re-exchange the access token through /api/auth. That would exchange this
    // fresh token a second time and trip replay-attack prevention, since
    // bff.getUser already performs the exchange explicitly.
    if (accessToken) {
      oktaAuth.tokenManager.add('accessToken', accessToken);
    }

    if (idToken) {
      oktaAuth.tokenManager.add('idToken', idToken);
    }

    if (refreshToken) {
      oktaAuth.tokenManager.add('refreshToken', refreshToken);
    }

    return accessToken?.accessToken;
  };

  return async function getUserImpl(token?: string, userId?: string) {
    // Track the token actually in use. If getUser recovers by refreshing the
    // Okta token, the refreshed token must also be used for getUserInfo, since
    // the original token may be expired or already exchanged.
    let currentToken = token;

    const fetchedUser = await bff.getUser(token, async () => {
      currentToken = await refreshOktaToken();

      return currentToken;
    });
    const fetchedUserInfo = await bff.getUserInfo(currentToken, userId);
    const profile = authState ? getUserProfile(authState) : undefined;

    // We know that for a WMG user the fetchedUser comes back with empty
    // values (except userLocale). So get the user info from the Okta
    // token instead.
    if (brand.name === 'WMG') {
      const oktaUser = await oktaAuth.getUser();

      return {
        name: oktaUser.name ?? '',
        email: oktaUser.email ?? '',
        isInternal: true, // WMG users are always internal.
        userId: fetchedUserInfo.userId,
        permissions: fetchedUser.permissions,
        profile,
      };
    }

    return {
      name: fetchedUser.name,
      email: fetchedUserInfo.email,
      permissions: fetchedUser.permissions,
      features: fetchedUserInfo.userFeatures,
      isInternal: fetchedUserInfo.isInternal,
      userId: fetchedUserInfo.userId,
      userType: fetchedUserInfo.userType,
      profile,
    };
  };
}

function WmgSessionProvider({ children }: PropsWithChildren) {
  const brand = useContext(BrandContext);
  const { authState, oktaAuth } = useOktaAuth();
  const [error, setError] = useState<string | undefined>();
  const [userString, setUserString] = useState('');
  const userRef = useRef<User>({
    name: '',
    email: '',
    isInternal: false,
    features: [],
    permissions: {},
    profile: undefined,
    userId: undefined,
    userType: undefined,
  });
  const isFetchingPermissions = useRef<boolean>(false);

  const oktaToken = authState?.accessToken?.accessToken;
  const oktaUserId = authState?.accessToken?.claims?.uid as string;

  const getUser = useMemo(
    () => getUserFactory(brand, oktaAuth, authState),
    [brand, oktaAuth, authState],
  );

  const authenticateUser = useCallback(
    async (token?: string, userId?: string) => {
      if (!token || !userId || isFetchingPermissions.current) {
        return;
      }

      setError(undefined);
      isFetchingPermissions.current = true;

      try {
        const userUpdates = await getUser(token, userId);

        userRef.current = {
          ...userRef.current,
          ...userUpdates,
        };

        setUserString(JSON.stringify(userRef.current));
      } catch (e) {
        setError((e as Error).message || 'Unexpected error');
      } finally {
        isFetchingPermissions.current = false;
      }
    },
    [getUser],
  );

  useEffect(() => {
    authenticateUser(oktaToken, oktaUserId);
  }, [authenticateUser, oktaToken, oktaUserId]);

  const sessionValue = useMemo(() => {
    return { user: userRef.current, token: oktaToken ?? '' };
    // We only want to re-compute when userString or oktaToken changes, as
    // userRef.current is a memory location that changes on every update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userString, oktaToken]);

  if (error) {
    return (
      <ErrorScreen
        message={error}
        onRetry={() => authenticateUser(oktaToken, oktaUserId)}
      />
    );
  }

  if (
    !oktaToken ||
    !userString ||
    (userString && isFetchingPermissions.current)
  ) {
    return <LoadingScreen />;
  }

  return (
    <WmgSessionContext.Provider value={sessionValue}>
      {children}
    </WmgSessionContext.Provider>
  );
}

WmgSessionProvider.displayName = 'WmgSessionProvider';

export { WmgSessionContext, WmgSessionProvider };
